/**
 * Component that renders travel rule input fields. This is triggered when a
 * user is sending more than $3000 with PayPal balance or credit card. Generally,
 * this component will require the user to supply a government ID (SSN, passport,
 * etc) and date of birth. If the user does not have a non-P.O. box address on
 * file, the user will also have to supply a non-P.O. box address.
 */

import PropTypes from 'prop-types'

import React from 'react'
import { connect } from 'transfer/utils/p2p-connect'
import { StyleSheet, css } from 'lib/paypalphrodite'
import TravelRuleGovernmentId from './travelRuleGovernmentId'
import TravelRuleAddress from './travelRuleAddress'
import { calculateAttributesToCollect } from '../utils/travelRuleUtils'
import { isDw2Repaint } from '../../experiments/dw2Repaint'
import { Container, BodyText } from '@paypalcorp/pp-react'

import content from 'pp-react-l10n'
import TravelRuleAddressFields from './travelRuleAddressFields'
import { hasGuestFlowRevamp } from '../../experiments/guest'

let i18n = content('transfer/send/identityInfo')

const styles = StyleSheet.create({
  identityInfo: {
    padding: '0px 36px',
    fontSize: '15px',
    '@media (max-width: 479px)': {
      padding: '0px 6px',
    },
  },
})

export class TravelRule extends React.Component {
  static propTypes = {
    selectedFundingOption: PropTypes.shape({
      contingencies: PropTypes.array,
    }),
    attemptedSubmit: PropTypes.bool.isRequired,
  }

  static defaultProps = { attemptedSubmit: false }

  // // used for testing (uncomment to render travel rule with govt id and dob)
  // getDefaultProps() {
  //   return {
  //     selectedFundingOption: {
  //       contingencies: [{
  //         action: 'PERFORM_DATA_COLLECTION',
  //         data_collection_details: { attributes: ['GOVT_ID', 'NON_PO_BOX_ADDRESS'] }
  //       }]
  //     }
  //   }
  // },

  render() {
    const attributesToCollect = calculateAttributesToCollect(
      this.props.selectedFundingOption
    )

    // if there are no attributes to collect (no travel rule), render nothing
    if (!attributesToCollect.length) {
      return null
    }

    const inputs = {
      GOVT_ID: <TravelRuleGovernmentId key="governmentId" {...this.props} />,
      NON_PO_BOX_ADDRESS:
        isDw2Repaint() || hasGuestFlowRevamp() ? (
          <TravelRuleAddressFields key="travelRuleAddress" {...this.props} />
        ) : (
          <TravelRuleAddress key="travelRuleAddress" {...this.props} />
        ),
    }

    if (isDw2Repaint() || hasGuestFlowRevamp()) {
      return (
        // PPReact's Container 'form' attribute gets rid of the horizontal margin if set to true.
        <Container form={hasGuestFlowRevamp()} data-nemo="identity-info">
          <BodyText strong style={{ margin: '1rem 0' }}>
            {i18n('identityInfo.identifiersCheckHeader')}
          </BodyText>
          <fieldset>
            <legend>
              <BodyText>{i18n('identityInfo.regulationsTitle')}</BodyText>
            </legend>
            {attributesToCollect.map(attribute => {
              return inputs[attribute] || null
            })}
          </fieldset>
        </Container>
      )
    }
    return (
      <div className={css(styles.identityInfo)} data-nemo="identity-info">
        <h2 className="header">{i18n('identityInfo.checkHeader')}</h2>
        <fieldset className="identity">
          <legend>{i18n('identityInfo.regulations')}</legend>
          {attributesToCollect.map(attribute => {
            return inputs[attribute] || null
          })}
        </fieldset>
      </div>
    )
  }
}

export default connect(({ user, transaction, address, server }) => ({
  selectedFundingOption: transaction.selectedFundingOption,
  selectedGovernmentIdType: user.selectedGovernmentIdType,
  governmentId: user.governmentId,
  birthDay: user.birthDay,
  birthMonth: user.birthMonth,
  birthYear: user.birthYear,
  addressEntryFormat: address.addressEntryFormat,
  newTravelRuleAddress: address.newTravelRuleAddress,
  country: server.country,
}))(TravelRule)
